/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useState } from 'react';

import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Input,
  Link,
  SimpleGrid,
  Stack,
  useToast,
} from '@chakra-ui/react';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { useForm } from 'react-hook-form';

import Logo from '@/assets/images/logo.png';
import FooterLinks from '@/components/Auth/FooterLinks';
import LanguageSelector from '@/components/Languages/LanguageSelector';
import { Button, Link as NavLink } from '@/elements';
import { useForgotPasswordMutation } from '@/generated/core.graphql';

import ForgotPasswordImage from './ForgottenPasswordImage.png';
import MessageSentImage from './MessageSentImage.png';

export default function ForgotPassword() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  const toast = useToast({ isClosable: true });
  const { t } = useTranslation('auth');

  const emailAddress = watch('email') ?? '';

  const [sendResetPasswordEmail, { loading }] = useForgotPasswordMutation();
  const [emailSent, setEmailSent] = useState(false);

  const resetPassword = useCallback(async () => {
    try {
      const success = await sendResetPasswordEmail({
        variables: {
          email: emailAddress,
        },
      });

      if (success) {
        setEmailSent(true);
        toast({
          status: 'success',
          title: t('toast.emailSent'),
          variant: 'top-accent',
          position: 'top-right',
        });
      }
    } catch (error) {
      toast({
        status: 'error',
        description: error.message,
        variant: 'top-accent',
        position: 'top-right',
      });
    }
  }, [emailAddress, sendResetPasswordEmail, t, toast]);

  const onSubmit = useCallback(
    async (values) => {
      if (values.email) {
        resetPassword();
      }
    },
    [resetPassword]
  );

  return (
    <SimpleGrid
      backgroundColor='white'
      columns={{ sm: 1, md: 2 }}
      height='100vh'
      overflowY={{ base: 'auto', md: 'hidden' }}
    >
      <Box
        display={{ base: 'none', md: 'initial' }}
        height='90%'
        margin='auto'
        width='90%'
      >
        <Image
          alt='intro image'
          src={ForgotPasswordImage}
          style={{ width: '100%', height: '100%' }}
        />
      </Box>
      <Box
        alignItems='center'
        display='flex'
        flexDirection='column'
        justifyContent='space-between'
        p={{
          base: 'md',
          sm: 'md',
          md: 'lg',
          lg: 'xl',
        }}
      >
        <Box />

        {!emailSent && (
          <Stack direction='column' spacing={4} width='28rem'>
            <Flex justifyContent='space-between'>
              <Image alt='logo image' height={873} src={Logo} width={289} />
              <LanguageSelector ml='xl' mt='md' />
            </Flex>
            <Box mb='4'>
              <Stack direction='column' spacing={2} width='28rem'>
                <NavLink
                  color='electricBlue.500'
                  data-cy='back-to-login'
                  fontWeight={600}
                  href='/auth/signin'
                >
                  &#60; {t('backToLogin')}
                </NavLink>
                <Heading fontWeight={700} size='lg'>
                  {t('titles.reset')}
                </Heading>
                <Box color='gray.500'>{t('subTitles.forgot')}</Box>
              </Stack>
            </Box>
            <Box>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack gap={3}>
                  <FormControl isInvalid={!!errors.email}>
                    <Input
                      {...register('email', {
                        required: t('validation.required'),
                      })}
                      fontSize='md'
                      id='email'
                      placeholder={t('placeholders.emailAddress')}
                      size='lg'
                      type='email'
                    />
                    {errors.email && (
                      <FormErrorMessage>
                        {errors.email.message as string}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </Stack>

                <Button
                  colorScheme='electricBlue'
                  data-cy='reset-password'
                  fontSize='md'
                  isLoading={loading}
                  minWidth={200}
                  mt='3rem'
                  size='lg'
                  type='submit'
                  variant='solid'
                  width='full'
                >
                  {t('buttons.resetPassword')}
                </Button>
              </form>
            </Box>
          </Stack>
        )}

        {emailSent && (
          <Stack spacing={1} width='28rem'>
            <Flex justifyContent='space-between'>
              <Image
                alt='email sent'
                src={MessageSentImage}
                style={{ width: '70%', height: '70%' }}
              />
              <LanguageSelector ml='xl' mt='md' />
            </Flex>
            <Box mb='2'>
              <Stack spacing={2}>
                <NavLink
                  color='electricBlue.500'
                  data-cy='back-to-login'
                  fontWeight={600}
                  href='/auth/signin'
                >
                  &#60; {t('backToLogin')}
                </NavLink>
                <Heading fontWeight={700} size='lg'>
                  {t('forgot.gotEmail')}
                </Heading>
                <Box color='gray.500'>
                  {t('forgot.matching')} <b>{emailAddress}</b>.<br />
                  {t('forgot.checkEmail')}
                </Box>
                <Box color='gray.500'>
                  {t('forgot.reciveEmail')}{' '}
                  <Link
                    color='electricBlue.500'
                    data-cy='resend-email'
                    fontWeight={600}
                    onClick={resetPassword}
                  >
                    {t('buttons.resendEmail')}
                  </Link>{' '}
                </Box>
              </Stack>
            </Box>
          </Stack>
        )}

        <FooterLinks />
      </Box>
    </SimpleGrid>
  );
}
